import { useQuery } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import to from 'await-to-js'
import { DeleteIcon } from 'components/Buttons/ActionButtons'
import { DataTable, DataTableProvider } from 'components/DataTable'
import dialogConfirm from 'components/dialogConfirm'
import { OverflownTextTooltip } from 'components/OverflownTextTooltip'
import { Tooltip } from 'components/Tooltip/tooltip'
import { DateCell } from 'components/Tables/CommonTableCells/DateCell'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import apiService from 'services/api'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { getTableQueryKey } from 'utils/getTableQueryKey'
import { dataTableSortingStoreRepo } from '../../../components/DataTable/DataTableSorting/DataTableSortingStore'
import { DataTableContainer } from '../../../components/DataTable/UI'
import FbButton from '../../../components/FbUI/FbButton'
import { Modal } from '../../../components/UI/Modal/Modal'
import { FiRefreshCw, FiKey, FiCheckCircle, FiSettings } from 'react-icons/fi'
import { CiWarning } from 'react-icons/ci'
import { GrantConnection } from 'models/grant'
import { AddAccountModal } from './AddAccountModal'
import { ManageConnectionModal } from './ManageConnectionModal'
import { SelectCalendarModal } from './SelectCalendarModal'
import * as S from './styles'

const PAGE_SIZE = 25
const tableKey = 'connections-table'
const sortableFields = ['email', 'updated_at', 'created_at']

function ConnectionsTableComponent() {
  const api = apiService()
  const [showAddAccountModal, setShowAddAccountModal] = useState(false)
  const [manageGrant, setManageGrant] = useState<GrantConnection | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [calendarCallbackEmail, setCalendarCallbackEmail] = useState<
    string | null
  >(null)
  const [selectCalendarIntegrationId, setSelectCalendarIntegrationId] =
    useState<number | null>(null)
  const { sorting } = dataTableSortingStoreRepo.getStore(tableKey)()

  const sortParams = useMemo(() => {
    const params: Record<string, string> = {}
    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }
    return params
  }, [sorting])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const success = urlParams.get('success')
    const error = urlParams.get('error')
    if (success) {
      const features = urlParams.get('features') ?? ''
      const email = urlParams.get('email')
      if (features.includes('calendar') && email) {
        // Calendar was just connected — wait for grants to load, then show picker
        setCalendarCallbackEmail(email)
      } else {
        setShowSuccessModal(true)
      }
      window.history.replaceState({}, '', `${window.location.pathname}`)
    } else if (error) {
      const message = urlParams.get('message')
      if (error === 'insufficient_permissions') {
        toast.error(
          message ||
            'You must allow all requested permissions to integrate your account'
        )
      } else {
        toast.error(
          'There was an issue connecting your account. Make sure you grant the necessary permissions and try again.'
        )
      }
      window.history.replaceState({}, '', `${window.location.pathname}`)
    }
  }, [])

  const [page, setPage] = useState(1)

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey,
    filterParams: sortParams,
    page,
  })

  const { isFetching, data, refetch } = useQuery({
    queryKey: TABLE_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const [err, data] = await to(
        api.grants.getGrantsList(
          {
            ...sortParams,
            limit: PAGE_SIZE,
            page,
          },
          signal
        )
      )
      if (err) throw err
      return data
    },
  })

  // Once grants data loads after a calendar callback, find the matching grant
  // and open the calendar selection modal.
  useEffect(() => {
    if (!calendarCallbackEmail || !data?.results) return
    const grant = data.results.find(
      (g) => g.email === calendarCallbackEmail && g.calendar_integration_id
    )
    if (grant?.calendar_integration_id) {
      setSelectCalendarIntegrationId(grant.calendar_integration_id)
      setCalendarCallbackEmail(null)
    }
  }, [calendarCallbackEmail, data])

  const onAddAccountContinue = async (
    provider: string,
    features: string[],
    isSharedValue: boolean
  ) => {
    const [err, response] = await to(
      api.emailIntegration.getEmailIntegrationAuthUrl({
        provider,
        isShared: isSharedValue,
        features,
      })
    )
    if (err) {
      toast.error('Error connecting account')
      return
    }
    if (response?.auth_url) {
      window.location.href = response.auth_url
    }
  }

  async function handleDelete(grant: GrantConnection) {
    const confirmed = await dialogConfirm(
      'Removing this connection will delete all email and calendar data associated with this account. This action cannot be undone.',
      'Delete Connection?'
    )
    if (!confirmed) return

    const [err] = await to(api.grants.deleteGrant(grant.grant_id))
    if (err) {
      toast.error('Error deleting connection')
      return
    }
    toast.success('Connection deleted')
    await refetch()
  }

  async function handleSync(grant: GrantConnection) {
    if (!grant.email_integration_id) {
      toast.error('No email integration to sync')
      return
    }
    const [err] = await to(
      api.emailIntegration.syncEmailIntegration(grant.email_integration_id)
    )
    if (err) {
      toast.error('Error syncing emails')
      return
    }
    toast.info(
      'Started syncing emails for this account. You will be notified when it is done.'
    )
    await refetch()
  }

  async function handleReauthenticate(grant: GrantConnection) {
    if (!grant.provider) {
      toast.error(
        'Unable to reauthenticate: provider information not available'
      )
      return
    }
    const features: string[] = []
    if (grant.has_email) features.push('email')
    if (grant.has_calendar) features.push('calendar')

    const [err, response] = await to(
      api.emailIntegration.getEmailIntegrationAuthUrl({
        provider: grant.provider,
        isShared: grant.is_shared,
        email: grant.email,
        features,
      })
    )
    if (err) {
      toast.error('Error creating reauthentication URL')
      return
    }
    if (response?.auth_url) {
      window.location.href = response.auth_url
    }
  }

  const columnHelper = createColumnHelper<GrantConnection>()

  const descriptionText =
    'Manage your connected accounts. Each connection can have email and/or calendar features enabled.'

  return (
    <DataTableContainer>
      <DataTable
        tableHeader={
          <S.SettingsTableHeader>
            <S.TableDescriptionText>
              <OverflownTextTooltip
                tooltipText={descriptionText}
                maxLines={4}
                placement="bottom"
              >
                {descriptionText}
              </OverflownTextTooltip>
            </S.TableDescriptionText>
            <FbButton onClick={() => setShowAddAccountModal(true)}>
              Add new account
            </FbButton>
          </S.SettingsTableHeader>
        }
        tableKey={tableKey}
        data={data?.results ?? []}
        columns={[
          columnHelper.accessor('email', {
            header: 'Email',
            size: 200,
          }),
          columnHelper.accessor('provider', {
            header: 'Provider',
            size: 100,
            cell: (info) => {
              const value = info.getValue()
              if (value === 'google') return 'Google'
              if (value === 'microsoft') return 'Outlook'
              return value
            },
          }),
          columnHelper.display({
            id: 'features',
            header: 'Features',
            size: 150,
            cell: ({ row }) => {
              const g = row.original
              return (
                <div className="flex gap-1">
                  {g.has_email && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      Email
                    </span>
                  )}
                  {g.has_calendar && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      Calendar
                    </span>
                  )}
                </div>
              )
            },
          }),
          columnHelper.accessor('is_shared', {
            header: 'Shared',
            size: 80,
            cell: ({ row }) => (row.original.is_shared ? 'Yes' : 'No'),
          }),
          columnHelper.display({
            id: 'status',
            header: 'Status',
            size: 100,
            cell: ({ row }) => {
              const g = row.original
              const statusMap: Record<string, string> = {
                synced: 'Synced',
                syncing: 'Syncing',
                not_synced: 'Not Synced',
              }

              // Show most relevant status
              const primaryStatus = g.email_status || g.calendar_status
              if (!primaryStatus) return '-'

              const colorClass =
                primaryStatus === 'synced'
                  ? 'text-green-500'
                  : primaryStatus === 'syncing'
                    ? 'text-yellow-500'
                    : 'text-red-500'

              return (
                <span className={colorClass}>
                  {statusMap[primaryStatus] ?? primaryStatus}
                </span>
              )
            },
          }),
          columnHelper.accessor('is_expired', {
            header: 'Connection',
            size: 120,
            cell: (info) => {
              const isExpired = info.getValue()
              const grant = info.row.original

              if (isExpired) {
                return (
                  <Tooltip
                    title="Connection broken"
                    content="Click to reauthenticate your account"
                    placement="top"
                  >
                    <div
                      onClick={() => handleReauthenticate(grant)}
                      className="cursor-pointer flex items-center text-red-500"
                    >
                      <CiWarning size={16} className="mr-1" />
                      <span>Disconnected</span>
                    </div>
                  </Tooltip>
                )
              }

              return (
                <div className="flex items-center text-green-500">
                  <FiCheckCircle size={16} className="mr-1" />
                  <span>Connected</span>
                </div>
              )
            },
          }),
          columnHelper.accessor('updated_at', {
            header: 'Modified',
            size: 100,
            cell: DateCell,
          }),
          columnHelper.display({
            id: 'actions',
            header: 'Actions',
            meta: { rightAlign: true },
            cell: ({ row }) => {
              const g = row.original
              return (
                <div className="flex justify-end">
                  <FiSettings
                    size={20}
                    color="#667085"
                    className="cursor-pointer mr-3"
                    title="Manage"
                    onClick={() => setManageGrant(g)}
                  />
                  <FiKey
                    size={20}
                    color="#667085"
                    className="cursor-pointer mr-3"
                    title="Reauthenticate"
                    onClick={() => handleReauthenticate(g)}
                  />
                  {g.has_email && (
                    <FiRefreshCw
                      size={20}
                      color="#667085"
                      className="cursor-pointer mr-3"
                      title="Sync emails"
                      onClick={() => handleSync(g)}
                    />
                  )}
                  <DeleteIcon onClick={() => handleDelete(g)} />
                </div>
              )
            },
            size: 150,
          }),
        ]}
        sortableFields={sortableFields}
        isPaginationEnabled={true}
        paginationOptions={{
          totalRows: data?.count ?? 0,
          pageSize: PAGE_SIZE,
          setPage,
          page,
          isPaginationLoading: isFetching,
        }}
        loading={isFetching}
      />
      <AddAccountModal
        open={showAddAccountModal}
        onClose={() => setShowAddAccountModal(false)}
        onContinue={onAddAccountContinue}
      />
      <ManageConnectionModal
        open={!!manageGrant}
        grant={manageGrant}
        onClose={() => setManageGrant(null)}
        onRefetch={() => void refetch()}
      />
      <SelectCalendarModal
        open={!!selectCalendarIntegrationId}
        integrationId={selectCalendarIntegrationId}
        onClose={() => {
          setSelectCalendarIntegrationId(null)
          setShowSuccessModal(true)
        }}
        onSelected={() => {
          setSelectCalendarIntegrationId(null)
          void refetch()
        }}
      />
      {showSuccessModal && (
        <Modal
          open={showSuccessModal}
          title="Success"
          description="Account connected successfully"
          onOpenChange={(open) => !open && setShowSuccessModal(false)}
          onAccept={() => setShowSuccessModal(false)}
          acceptButtonText="Close"
          hideCancelButton
        />
      )}
    </DataTableContainer>
  )
}

export function ConnectionsTable() {
  return (
    <ColumnsStoreProvider tableKey={tableKey}>
      <DataTableProvider tableKey={tableKey}>
        <ConnectionsTableComponent />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
